/* eslint-disable no-param-reassign */
import store from '@/store';
import { translate } from '@/translation/native_translate';
import doRequest from '@/api/dssRequest';
import UserObject from '@/objects/UserObject';
import utility from '@/shared/utility';

function onCancelReservationResponse(response, payload, ordersInCart, index) {
  // removes the selected item from the shoppingCart
  if (ordersInCart[index].ShoppingCartType === 'Product') {
    if (ordersInCart[index].Products.length === 1) {
      // Only one item in the order, remove the order
      ordersInCart.splice(index, 1);
    } else {
      // to find the product and remove it from the array
      const indexOfItem = ordersInCart[index].Products.findIndex((item) => {
        if (productHasOptions(item)) {
          return item.OrderItemId === payload.OrderItemId;
        }
        return item.ItemId === payload.ItemId;
      });
      ordersInCart[index].Products.splice(indexOfItem, 1);
      // Update OrderTotalPrice
      ordersInCart[index].OrderTotalPrice =
        response.data.MainOrder.OrderTotalPrice;
      ordersInCart[index].OrderTotalPriceVat =
        response.data.MainOrder.OrderTotalPriceVat;
    }
  } else {
    // remove ticket order from shoppingCart orders
    ordersInCart.splice(index, 1);
  }

  // Commit orders changes in store
  store.commit('shoppingCart/setOrders', ordersInCart);

  window.EventBus.fire('activateSnackBar', {
    text: translate('CARTITEM_REMOVED'),
    color: 'success',
  });
}

function cancelReservation(payload, request, ordersInCart, index) {
  doRequest(
    (response) => {
      onCancelReservationResponse(response, payload, ordersInCart, index);
    },
    () => {
      // Reset orders
      store.commit('shoppingCart/setOrders', ordersInCart);
      window.EventBus.fire('activateSnackBar', {
        text: translate('CARTITEM_NOT_REMOVED'),
        color: 'error',
      });
    },
    request,
    UserObject.getUserObjectId(),
    payload
  );
}

function getOrderByProduct(product, orders) {
  return orders.find((order) => order.EventId === product.EventId);
}

function getProductInOrder(itemId, order) {
  return order.Products.find((productItem) => productItem.ItemId === itemId);
}

function getProductInOrderByOrderItemId(orderItemId, order) {
  return order.Products.find(
    (productItem) => productItem.OrderItemId === orderItemId
  );
}

function getProductIndexInOrderByOrderItemId(orderItemId, order) {
  return order.Products.findIndex(
    (productItem) => productItem.OrderItemId === orderItemId
  );
}

function productHasOptionSelections(product) {
  return product.OptionSelection != null && product.OptionSelection.length > 0;
}

function productHasOptions(product) {
  return product.Options != null && product.Options.length > 0;
}

function getProductInOrders(product, orders) {
  const order = getOrderByProduct(product, orders);
  if (order != null) {
    if (productHasOptionSelections(product) || productHasOptions(product)) {
      return getProductInOrderByOrderItemId(product.OrderItemId, order);
    }
    return getProductInOrder(product.ItemId, order);
  }
  return null;
}

function onChangeProductError(error) {
  let errorMsg = '';
  switch (error.data.ErrorCode) {
    case 'E_PO_006':
      errorMsg = translate('E_PO_006_PRODUCT');
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    case 'E_PO_023':
      errorMsg = translate('E_PO_023_CHANGEPRODUCT');
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    case 'E_PO_024':
      errorMsg = translate('E_PO_024_CHANGEPRODUCT');
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    case 'E_PCAP_001':
      errorMsg = utility.sprintf(
        translate('E_PCAP_001_CHANGE_PROD'),
        translate('SHOPPING_CART_LOWERCASE')
      );
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    case 'E_PCAP_002':
      errorMsg = utility.sprintf(
        translate('E_PCAP_002_CHANGE_PROD'),
        translate('SHOPPING_CART_LOWERCASE')
      );
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    default:
      window.EventBus.fire('activateSnackBar', {
        text: translate('FAILED_ADD_CART'),
        color: 'error',
      });
      break;
  }
}

function changeProductOnReservation(product, ordersInCart) {
  const orderInCart = getOrderByProduct(product, ordersInCart);
  if (orderInCart == null || orderInCart.Products == null) {
    console.error(
      'Product owner not found in shopping cart: ',
      product,
      ordersInCart
    );
    store.commit('shoppingCart/setOrders', ordersInCart);
    return;
  }

  const productInCart = getProductInOrders(product, ordersInCart);
  if (productInCart == null) {
    console.error(
      'Product item not found in shopping cart: ',
      product,
      ordersInCart
    );
    store.commit('shoppingCart/setOrders', ordersInCart);
    return;
  }

  const payload = {
    ItemId: product.ItemId,
    OrderItemId: product.OrderItemId,
    EventId: product.EventId,
    OrderId: orderInCart.OrderId,
  };

  if (product.Type === 'Weighted') {
    payload.Quantity = product.Amount;
  } else {
    payload.Quantity = product.Quantity;
  }

  doRequest(
    (response) => {
      const productUpdate = response.data.MainOrder.Products.find((prod) => {
        if (productHasOptions(product)) {
          return prod.OrderItemId === product.OrderItemId;
        }
        return prod.ItemId === product.ItemId;
      });

      if (productUpdate != null) {
        productInCart.Quantity = productUpdate.Quantity;
        productInCart.ItemTotalPrice = productUpdate.ItemTotalPrice;
        productInCart.ItemTotalVatPrice = productUpdate.ItemTotalVatPrice;
      }

      orderInCart.OrderTotalPrice = response.data.MainOrder.OrderTotalPrice;
      orderInCart.OrderTotalPriceVat =
        response.data.MainOrder.OrderTotalPriceVat;
      orderInCart.Discounts = response.data.MainOrder.Discounts;

      // Commit orders changes in store
      store.commit('shoppingCart/setOrders', ordersInCart);

      window.EventBus.fire('activateSnackBar', {
        text: translate('ITEMS_ADDED_TO_BASKET'),
        color: 'success',
      });
    },
    (error) => {
      store.commit('shoppingCart/setOrders', ordersInCart);
      onChangeProductError(error);
    },
    'changeProductOnReservation',
    UserObject.getUserObjectId(),
    payload
  );
}

function onAddProductError(error) {
  let errorMsg = '';
  switch (error.data.ErrorCode) {
    case 'E_PO_006':
      errorMsg = translate('E_PO_006_PRODUCT');
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    case 'E_PO_023':
      errorMsg = translate('E_PO_023_ADDPRODUCT');
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    case 'E_PO_024':
      errorMsg = translate('E_PO_024_ADDPRODUCT');
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    case 'E_PCAP_001':
      // Quantity above max quantity
      errorMsg = utility.sprintf(
        translate('E_PCAP_001_ADD_PROD'),
        translate('SHOPPING_CART_LOWERCASE')
      );
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    case 'E_PCAP_002':
      // Quantity below min quantity
      errorMsg = utility.sprintf(
        translate('E_PCAP_002_ADD_PROD'),
        translate('SHOPPING_CART_LOWERCASE')
      );
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    default:
      window.EventBus.fire('activateSnackBar', {
        text: translate('FAILED_ADD_CART'),
        color: 'error',
      });
      break;
  }
}

function addProduct(payload, cartItem, shoppingCart) {
  let existingOrderIndex = null;
  for (let i = 0; i < shoppingCart.length; i++) {
    if (shoppingCart[i].EventId === cartItem.EventId) {
      payload.OrderId = shoppingCart[i].OrderId;
      existingOrderIndex = i;
      break;
    }
  }

  payload.ProductId = cartItem.Id;
  payload.ItemId = cartItem.ItemId;
  payload.EventId = cartItem.EventId;
  cartItem.Type === 'Weighted'
    ? (payload.Quantity = cartItem.Amount)
    : (payload.Quantity = cartItem.Quantity);

  if (productHasOptionSelections(cartItem)) {
    payload['OptionSelection'] = cartItem.OptionSelection;
  }

  doRequest(
    (response) => {
      const _viewType = store.getters['Account/partnerGroupDetails'].ViewType;
      if (_viewType === 'ProfessionalBeta') {
        window.EventBus.fire('activateSnackBar', {
          text: translate('ITEMS_ADDED_TO_BASKET'),
          color: 'success',
        });
      } else {
        window.EventBus.fire('shoppingcartDialog');
      }
      // remove the selected shoppingList
      store.commit('setCurrentSelectedShoppingList', {});

      if (payload.OrderId !== '') {
        response.data.MainOrder.Products.forEach((product) => {
          response.data.MainOrder.CurrentProductInfo.forEach((currentInfo) => {
            if (
              currentInfo.ItemId === product.ItemId &&
              currentInfo.ProductId === product.ProductId
            ) {
              //product.Picture = currentInfo.Picture;

              if (Object.keys(currentInfo.ItemDefs).length > 0) {
                product.ItemDefs = currentInfo.ItemDefs;
              }

              if (cartItem.WeightedInfo != null) {
                product.WeightedInfo = currentInfo.WeightedInfo;
              }

              product.Product = currentInfo;
              product.Item = currentInfo.Items[0];
            }
          });

          // Check if the item is already in the ShoppingCart, by searching for the OrderItemId.
          // Products that have ProductOptions, have the same ItemId, but differ in OrderItemId.
          // The BackEnd sends us back all ProductItems, we don't want to add the already existing ones again to the ShoppingCart.
          let productIndexInCart = getProductIndexInOrderByOrderItemId(
            product.OrderItemId,
            shoppingCart[existingOrderIndex]
          );

          const foundProductInCart = productIndexInCart >= 0;

          if (!foundProductInCart) {
            shoppingCart[existingOrderIndex].Products.push(product);
          } else {
            shoppingCart[existingOrderIndex].Products[productIndexInCart] =
              product;
          }
        });

        shoppingCart[existingOrderIndex].OrderTotalPrice =
          response.data.MainOrder.OrderTotalPrice;
        shoppingCart[existingOrderIndex].OrderTotalPriceVat =
          response.data.MainOrder.OrderTotalPriceVat;
        shoppingCart[existingOrderIndex].Discounts =
          response.data.MainOrder.Discounts;
        shoppingCart[existingOrderIndex].DepositValue =
          response.data.MainOrder.DepositValue;
        shoppingCart[existingOrderIndex].Date = new Date();

        store.commit('shoppingCart/setOrders', shoppingCart);
      } else {
        // check for eventType Virtual if virtual do not set shoppingCartType to product
        const toStoreOrder = {
          Date: new Date(),
          EventId: cartItem.EventId,
          OrderId: response.data.MainOrder.OrderId,
          OrderTotalPrice: response.data.MainOrder.OrderTotalPrice,
          OrderTotalPriceVat: response.data.MainOrder.OrderTotalPriceVat,
          DepositValue: response.data.MainOrder.DepositValue,
          PriceCategories: response.data.MainOrder.PriceCategories,
          Products: response.data.MainOrder.Products,
          // if product type is virtual set not to Product to follow the activities flow
          ShoppingCartType:
            response.data.EventInfo.Type === 'VirtualProductShop'
              ? 'VirtualProduct'
              : 'Product',
          Discounts: response.data.MainOrder.Discounts,
        };

        // set the picture of the webshop
        if (response.data.EventInfo.Type === 'Webshop') {
          toStoreOrder.Products[0].Picture = cartItem.Picture;
          if (Object.keys(cartItem.ItemDefs).length > 0) {
            toStoreOrder.Products[0].ItemDefs = cartItem.ItemDefs;
          }
          if (cartItem.WeightedInfo != null) {
            toStoreOrder.Products[0].WeightedInfo = cartItem.WeightedInfo;
          }

          toStoreOrder.Products[0].Product = cartItem.Product;
          toStoreOrder.Products[0].Item = cartItem.Item;
        }

        shoppingCart.push(toStoreOrder);
      }
    },
    onAddProductError,
    'addProductToReservation',
    UserObject.getUserObjectId(),
    payload
  );
}

function addRentalOrTicket(payload, cartItem, shoppingCart) {
  payload.Note = cartItem.Note;
  payload.SpecialRequests = [];
  payload.Tickets = {};

  Object.values(cartItem.PriceCategories).forEach((ticket) => {
    if (ticket.Amount > 0) {
      Object.assign(payload.Tickets, {
        [ticket.Id]: Number(ticket.Amount),
      });
    }
  });

  if (cartItem.EventType === 'Rental') {
    payload.Products = cartItem.Products;
  }

  if (payload.EventId == null && cartItem.EventId != null) {
    payload.EventId = cartItem.EventId;
  }

  doRequest(
    (response) => {
      window.EventBus.fire('activateSnackBar', {
        text: translate('TICKETS_RESERVED'),
        color: 'success',
      });

      const toStoreOrder = {
        EventId: cartItem.EventId,
        Picture: response.data.Picture || response.data.EventInfo.PictureUrl,
        Name:
          response.data.Name || response.data.EventInfo.EventTranslationName,
        Date: new Date(),
        OrderId: response.data.MainOrder.OrderId,
        OrderTotalPrice: response.data.MainOrder.OrderTotalPrice,
        OrderTotalPriceVat: response.data.MainOrder.OrderTotalPriceVat,
        PriceCategories: response.data.MainOrder.PriceCategories,
        Products: response.data.MainOrder.Products,
        StartDate: response.data.MainOrder.StartDate,
        EndDate: response.data.MainOrder.EndDate,
        StartTime: response.data.MainOrder.StartTimeFE,
        EndTime: response.data.MainOrder.EndTimeFE,
      };
      if (response.data.EventInfo != null) {
        if (response.data.EventInfo.Type != null) {
          if (response.data.EventInfo.Type === 'Rental') {
            toStoreOrder.ShoppingCartType = 'Rental';
          } else {
            toStoreOrder.ShoppingCartType = 'Ticket';
          }
        }
      } else if (
        response.data.EventType != null &&
        response.data.EventType === 'Rental'
      ) {
        toStoreOrder.ShoppingCartType = 'Rental';
      } else {
        toStoreOrder.ShoppingCartType = 'Ticket';
      }
      // put item in the shoppingCart
      shoppingCart.push(toStoreOrder);
      // route to the order booking
      window.EventBus.fire('goToOrder');
    },
    (error) => {
      if (
        error.data.ErrorCode === 'E_PO_006' ||
        error.data.ErrorCode === 'E_CP_003'
      ) {
        window.EventBus.fire(
          'activateErrorDialog',
          translate('E_PO_006_TICKET'),
          false
        );
      } else {
        window.EventBus.fire('activateSnackBar', {
          text: translate('FAILED_ADD_CART'),
          color: 'error',
        });
      }
    },
    'reserve',
    UserObject.getUserObjectId(),
    payload
  );
}

function refreshShoppingCartOrders(orders) {
  let shoppingCart= []

  if(orders.length > 0) {
    orders.forEach(order => {
      let shoppingItem = {}
      shoppingItem.Date = new Date();
      shoppingItem.EventId = order.EventId;
      shoppingItem.OrderId = order.OrderId;
      shoppingItem.DepositValue = order.DepositValue;
      shoppingItem.Discounts = order.Discounts;
      shoppingItem.OrderTotalPrice = order.OrderTotalPrice;
      shoppingItem.OrderTotalPriceVat = order.OrderTotalPriceVat;
      shoppingItem.PriceCategories = order.PriceCategories;
      shoppingItem.Products = order.Products;
      shoppingItem.ShoppingCartType = order.Type === 'VirtualProductShop'
        ? 'VirtualProduct'
        : 'Product';
      shoppingCart.push(shoppingItem);
    });
  }
  store.commit('shoppingCart/setOrders', shoppingCart);
  console.log("ShoppingCart after refresh", shoppingCart);
}

const state = {
  orders: [],
  eventDetails: {},
};

const mutations = {
  addOrderWithoutReservation(state, order) {
    if (typeof state.orders[0] === 'undefined') {
      state.orders.push(order);
    } else {
      for (let i = 0; i < state.orders.length; i++) {
        if (state.orders[i].EventId === order.EventId) {
          // add products to this order
          order.Products.forEach((newProduct) => {
            let allowPush = true;
            state.orders[i].Products.forEach((product) => {
              var foundProduct = false;
              if (productHasOptions(product)) {
                if (product.OrderItemId === newProduct.OrderItemId) {
                  foundProduct = true;
                }
              } else {
                if (product.ItemId === newProduct.ItemId) {
                  foundProduct = true;
                }
              }

              if (foundProduct) {
                product.Quantity += newProduct.Quantity;
                product.ItemTotalPrice += newProduct.ItemTotalPrice;
                state.orders[i].OrderTotalPrice += newProduct.ItemTotalPrice;
                allowPush = false;
              }
            });
            if (allowPush) {
              // add the new product to the order if it doesnt already exist inside
              state.orders[i].Products.push(newProduct);
            }
          });
          break;
        } else if (typeof state.orders[i + 1] === 'undefined') {
          // add new order here
          state.orders.push(order);
          break;
        } else {
          // go next order
        }
      }
    }
  },
  addOrder(state, order) {
    const payload = {
      OrderId: '',
      StartTime: order.StartTime != null ? order.StartTime : '',
      EndTime: order.EndTime != null ? order.EndTime : '',
      StartDate: order.StartDate != null ? order.StartDate : '',
      EndDate: order.EndDate != null ? order.EndDate : '',
      StartBatchGroupId:
        order.StartBatchGroupId != null ? order.StartBatchGroupId : '',
      EndBatchGroupId:
        order.EndBatchGroupId != null ? order.EndBatchGroupId : '',
    };

    if (
      order.EventType !== 'Webshop' &&
      order.EventType !== 'VirtualProductShop'
    ) {
      addRentalOrTicket(payload, order, state.orders);
      return;
    }

    // Always add product, requested by Joris
    addProduct(payload, order, state.orders);

    // const productInCart = getProductInOrders(order, state.orders);

    // if (
    //   productInCart != null &&
    //   !(order.OptionSelection != null && order.OptionSelection.length > 0)
    // ) {
    //   // Product already exists in shopping cart, update quantity
    //   if (order.Type === 'Weighted') {
    //     order.Amount = productInCart.Quantity + order.Amount;
    //   } else {
    //     order.Quantity = productInCart.Quantity + order.Quantity;
    //   }

    //   changeProductOnReservation(order, state.orders);
    // } else {
    //   addProduct(payload, order, state.orders);
    // }
  },
  removeOrder(state, orderData) {
    const { itemId } = orderData;
    const { orderItemId } = orderData;
    const { orderIndex } = orderData;

    // removing order from the Orders store value
    const eventId = state.orders[orderIndex].EventId;

    const payload = {
      OrderId: state.orders[orderIndex].OrderId,
      EventId: eventId,
    };

    let request = 'cancelReservation';

    // tickets and rental does not contain itemId. A product however does contain an itemId
    if (state.orders[orderIndex].ShoppingCartType === 'Product') {
      payload.ItemId = itemId;
      payload.OrderItemId = orderItemId;
      request = 'removeProductFromReservation';
    }

    // call the function cancelreservation or removeproductfromrervation depends on the objectid
    cancelReservation(payload, request, state.orders, orderIndex);
  },
  setOrders(state, orders) {
    state.orders = [];
    if (orders != null) {
      state.orders = [...orders];
    }
  },
  changeProduct(state, product) {
    changeProductOnReservation(product, state.orders);
  },
  setEventDetails(state, eventDetails) {
    state.eventDetails = eventDetails;
  },
  refreshOrders(state, orders) {
    refreshShoppingCartOrders(orders);
  }
};

const getters = {
  orders(state) {
    return state.orders;
  },
  eventDetails(state) {
    return state.eventDetails;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
};
